/**
 * 签名令牌(HMAC-SHA256)—— edge token 与 probe token 共用的无状态交叉校验载体。
 *
 * 形态:`base64url(JSON payload) + '.' + base64url(hmac)`。worker 签发,客户端原样
 * 带回,worker 验签后用载荷里"签发时刻的边缘信号"与 verify 时刻做跨请求交叉校验,
 * 替代存储回读(v1 的 getProbe / device-stats probe 路径)。
 *
 * 载荷必须含 `iat`(签发毫秒时间戳);`verifyToken` 同时校验签名与 TTL。
 * 仅依赖 Web Crypto,Workers / Pages Functions / Node ≥18 / 浏览器通用。
 */

/** 令牌通用载荷:sid 绑定会话(防跨会话重放),iat 为签发时刻。 */
export interface SignedTokenPayload {
  sid: string;
  iat: number;
}

/** edge token:HTML 请求时刻的边缘信号快照(middleware 经 worker 签发,注入 HTML)。 */
export interface EdgeTokenPayload extends SignedTokenPayload {
  ip?: string;
  country?: string;
  asn?: number;
  ja4?: string;
}

/** probe token:probe 请求时刻的边缘信号快照(probe 响应下发,verify 带回)。 */
export interface ProbeTokenPayload extends SignedTokenPayload {
  ip?: string;
  country?: string;
  asn?: number;
  acceptLanguage?: string;
}

const encoder = new TextEncoder();

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

export async function signToken(payload: SignedTokenPayload, secret: string): Promise<string> {
  const body = toBase64Url(encoder.encode(JSON.stringify(payload)));
  const mac = await crypto.subtle.sign(
    { name: 'HMAC' },
    await hmacKey(secret),
    encoder.encode(body),
  );
  return `${body}.${toBase64Url(new Uint8Array(mac))}`;
}

/**
 * 验签 + TTL 校验。任何失败(格式/签名/过期/载荷缺 iat)都返回 null —— 调用方把
 * null 当"无令牌"信号处理(引擎扣分或降级),绝不抛错。
 */
export async function verifyToken<T extends SignedTokenPayload>(
  token: string,
  secret: string,
  maxAgeMs: number,
  now = Date.now(),
): Promise<T | null> {
  try {
    const dot = token.indexOf('.');
    if (dot <= 0 || dot === token.length - 1) return null;
    const body = token.slice(0, dot);
    const mac = fromBase64Url(token.slice(dot + 1));
    const ok = await crypto.subtle.verify(
      { name: 'HMAC' },
      await hmacKey(secret),
      mac as BufferSource,
      encoder.encode(body),
    );
    if (!ok) return null;
    const payload = JSON.parse(new TextDecoder().decode(fromBase64Url(body))) as T;
    if (typeof payload.iat !== 'number' || typeof payload.sid !== 'string') return null;
    if (now - payload.iat > maxAgeMs || payload.iat - now > 60_000) return null;
    return payload;
  } catch {
    return null;
  }
}

/** edge token 的有效期:覆盖正常会话生命周期,过期视同缺失(降级,不拦截)。 */
export const EDGE_TOKEN_MAX_AGE_MS = 15 * 60_000;
/** probe token 的有效期:probe → verify 间隔通常秒级。 */
export const PROBE_TOKEN_MAX_AGE_MS = 15 * 60_000;
